import { type User } from '@/userManagement/domain/entities/User';
import { type PermissionDeviation } from '@/userManagement/domain/entities/Permission';
import { type UserTableData } from '../types/UserTableData';
import type { UserWireDto } from '../types/UserWireDto';

export type { UserWireDto } from '../types/UserWireDto';

function roleNamesForTable(user: User): string[] {
  if (user.permissionRoleNames?.length) {
    return user.permissionRoleNames;
  }
  const single = user.permissionRoleName?.trim();
  if (single) {
    return [single];
  }
  return [];
}

/** List/detail wire may use `id`, `crossUserId`, or snake_case — match {@link mapUserWireToEntity} so `User.id` is never dropped when the API omits `id`. */
function resolveUserWireId(dto: UserWireDto): string {
  const r = dto as unknown as Record<string, unknown>;
  const raw = r.crossUserId ?? r.cross_user_id ?? r.id ?? dto.id;
  if (raw === null || raw === undefined) return '';
  return String(raw).trim();
}

export function mapUserDtoToEntity(dto: UserWireDto): User {
  return {
    id: resolveUserWireId(dto),
    username: dto.username,
    firstName: dto.firstName,
    lastName: dto.lastName,
    email: dto.email,
    phone: dto.phone,
    customId: dto.customId ?? null,
    permissionRoleId: dto.permissionRoleId,
    permissionRoleName: dto.permissionRoleName,
    permissionRoleNames: dto.permissionRoleNames,
    isLocked: dto.isLocked,
    isSuspended: dto.isSuspended,
    ...(dto.isSystemAdmin !== undefined ? { isSystemAdmin: dto.isSystemAdmin } : {}),
    isActive: dto.isActive ?? true,
    forceNewPassword: dto.forceNewPassword,
    failedLoginAttempts: dto.failedLoginAttempts ?? 0,
    lastLoginOn: dto.lastLoginOn,
    assignedDatabases: dto.assignedDatabases ?? [],
    tenantDatabaseEntries: dto.tenantDatabaseEntries,
    utcUpdatedOn: dto.utcUpdatedOn,
    deviationsPreview: dto.deviationsPreview,
    databaseRegistrations: dto.databaseRegistrations,
    ...(dto.inProgressApprovalRequestCount !== undefined ? { inProgressApprovalRequestCount: dto.inProgressApprovalRequestCount } : {}),
    ...(dto.approvedApprovalRequestCount !== undefined ? { approvedApprovalRequestCount: dto.approvedApprovalRequestCount } : {}),
    ...(dto.pendingNewRoleId !== undefined ? { pendingNewRoleId: dto.pendingNewRoleId } : {}),
    ...(dto.pendingNewRoleName !== undefined ? { pendingNewRoleName: dto.pendingNewRoleName } : {})
  };
}

function hasPendingRoleChange(user: User): boolean {
  const name = user.pendingNewRoleName?.trim();
  if (name) return true;
  return user.pendingNewRoleId != null && user.pendingNewRoleId > 0;
}

export function mapUserToTableData(user: User, deviations: PermissionDeviation[], hasPendingChanges?: boolean): UserTableData {
  const fromList = user.deviationsPreview ?? deviations;
  const deviationCount = fromList.length;
  const hasAbove = fromList.some((d) => d.direction === 'above');
  const hasBelow = fromList.some((d) => d.direction === 'below');

  let deviationType: UserTableData['deviationType'] = null;
  if (hasAbove && hasBelow) {
    deviationType = 'mixed';
  } else if (hasAbove) {
    deviationType = 'above';
  } else if (hasBelow) {
    deviationType = 'below';
  }

  const pending = hasPendingChanges ?? hasPendingRoleChange(user);

  return {
    id: user.id,
    username: user.username,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone,
    email: user.email,
    roleName: user.permissionRoleName,
    roleNames: roleNamesForTable(user),
    roleId: user.permissionRoleId,
    databases: user.assignedDatabases,
    databaseEntries:
      user.tenantDatabaseEntries ?? user.assignedDatabases.map((tenantDatabaseName) => ({ tenantDatabaseName, databaseAlias: null })),
    lastLoginOn: user.lastLoginOn,
    isLocked: user.isLocked,
    isSuspended: user.isSuspended,
    ...(user.isSystemAdmin !== undefined ? { isSystemAdmin: user.isSystemAdmin } : {}),
    isActive: user.isActive,
    forceNewPassword: user.forceNewPassword,
    failedLoginAttempts: user.failedLoginAttempts,
    deviationCount,
    deviationType,
    hasPendingChanges: pending,
    ...(user.inProgressApprovalRequestCount !== undefined ? { inProgressApprovalRequestCount: user.inProgressApprovalRequestCount } : {}),
    ...(user.approvedApprovalRequestCount !== undefined ? { approvedApprovalRequestCount: user.approvedApprovalRequestCount } : {}),
    ...(user.pendingNewRoleId !== undefined ? { pendingNewRoleId: user.pendingNewRoleId } : {}),
    ...(user.pendingNewRoleName !== undefined ? { pendingNewRoleName: user.pendingNewRoleName } : {})
  };
}
